import { useEffect, useState } from "react";
import { Plus, Loader2, Search, Users, Trash2, Pencil, Car, Phone, Mail } from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import localApi from "@/lib/localApi";
import Layout from "@/components/Layout";
import { VehicleManager } from "@/components/VehicleManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const blank = {
  name: "", phone: "", email: "", business_name: "", business_address: "",
  vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_vin: "",
};

export default function Customers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [garageCustomer, setGarageCustomer] = useState(null);
  const [vehCounts, setVehCounts] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadVehCounts = async () => {
    try {
      const { data } = await localApi.get("/shop/vehicles/counts");
      setVehCounts(data.counts || {});
    } catch (e) { console.error("Failed to load vehicle counts", e); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/customers"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadVehCounts(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...blank, ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    const payload = { ...form };
    try {
      if (editing) { await api.put(`/customers/${editing.id}`, payload); toast.success("Customer updated"); }
      else { await api.post("/customers", payload); toast.success("Customer added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!toDelete) return;
    try { await api.delete(`/customers/${toDelete.id}`); setItems((x) => x.filter((c) => c.id !== toDelete.id)); toast.success("Customer deleted"); }
    catch { toast.error("Failed to delete"); }
    finally { setToDelete(null); }
  };

  const filtered = items.filter((c) =>
    !q || [c.name, c.phone, c.email, c.vehicle_make, c.vehicle_model].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
  );
  const field = "h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  return (
    <Layout
      title="Customers & Vehicles"
      action={<Button data-testid="new-customer-button" onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Customer</Button>}
    >
      <div className="space-y-4 animate-fade-up">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers or vehicles" className="pl-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-16 text-center">
            <Users className="h-10 w-10 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400 mb-4">No customers yet.</p>
            <Button onClick={openNew} data-testid="empty-add-customer" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add customer</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((c) => {
              const vehicle = [c.vehicle_year, c.vehicle_make, c.vehicle_model].filter(Boolean).join(" ");
              return (
                <div key={c.id} data-testid={`customer-card-${c.id}`}
                  className="rounded-xl border border-slate-800 bg-slate-900 p-5 transition-transform duration-200 hover:-translate-y-[2px]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-slate-800 flex items-center justify-center text-amber-400 font-bold shrink-0">
                        {c.name.slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-100 truncate">{c.name}</p>
                        {c.business_name && <p className="text-xs text-slate-500 truncate">{c.business_name}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" data-testid={`edit-customer-${c.id}`} onClick={() => openEdit(c)} className="h-8 w-8 text-slate-500 hover:text-amber-400 hover:bg-slate-800"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" data-testid={`delete-customer-${c.id}`} onClick={() => setToDelete(c)} className="h-8 w-8 text-slate-500 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5 text-sm text-slate-400">
                    {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-600" /> {c.phone}</p>}
                    {c.email && <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-slate-600" /> {c.email}</p>}
                    <p className="flex items-center gap-2"><Car className="h-3.5 w-3.5 text-slate-600" /> {vehicle || "No vehicle on file"}</p>
                    {c.vehicle_vin && <p className="text-xs text-slate-600 pl-[22px]">VIN: {c.vehicle_vin}</p>}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between">
                    <span className="text-[11px] text-slate-600">Added {fmtDate(c.created_at)}</span>
                    <Button size="sm" variant="ghost" data-testid={`vehicles-customer-${c.id}`} onClick={() => setGarageCustomer(c)}
                      className="text-amber-400 hover:text-amber-300 hover:bg-slate-800 h-7"><Car className="h-3.5 w-3.5 mr-1" /> Vehicles{vehCounts[c.id] ? ` (${vehCounts[c.id]})` : ""}</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-100 font-head">{editing ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Name *</Label><Input data-testid="customer-name-input" value={form.name} onChange={set("name")} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Phone</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} className={field} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Email</Label><Input value={form.email} onChange={set("email")} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Business name</Label><Input value={form.business_name} onChange={set("business_name")} className={field} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Address</Label><Input data-testid="customer-address-input" value={form.business_address} onChange={set("business_address")} className={field} /></div>
            <div className="pt-2 border-t border-slate-800">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-3 flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> Vehicle</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Year</Label><Input value={form.vehicle_year} onChange={set("vehicle_year")} className={field} /></div>
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Make</Label><Input data-testid="customer-make-input" value={form.vehicle_make} onChange={set("vehicle_make")} className={field} /></div>
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Model</Label><Input value={form.vehicle_model} onChange={set("vehicle_model")} className={field} /></div>
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">VIN</Label><Input data-testid="customer-vin-input" value={form.vehicle_vin} onChange={set("vehicle_vin")} className={field} /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</Button>
            <Button data-testid="save-customer-button" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Delete customer?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">{toDelete?.name} will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer" onClick={remove} className="bg-rose-600 hover:bg-rose-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {garageCustomer && (
        <VehicleManager customer={garageCustomer} onClose={() => setGarageCustomer(null)} onChanged={loadVehCounts} />
      )}
    </Layout>
  );
}
